import { useToast } from '@lifebit-ai/lds'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { extractErrorMessage } from 'core/utils'
import { rejectDataAccessRequest } from 'services/data-access-requests'
import { DataAccessRequestQueryKeys } from 'services/data-access-requests/queryKeys'
import type { RejectDataAccessRequestParams } from 'services/data-access-requests/types'
import { invalidateRequestActivityQueries } from 'services/request-activity/invalidateRequestActivityQueries'
import type { ApiErrorResponse } from 'services/types'
import { isAirlockRequestsV3RoutesEnabled } from 'utils/env'

interface UseRejectDataAccessRequestParams {
  onSuccess?: () => void
  requestId: string
}

export const useRejectDataAccessRequest = ({ onSuccess, requestId }: UseRejectDataAccessRequestParams) => {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation<void, ApiErrorResponse, RejectDataAccessRequestParams>({
    mutationFn: ({ reason }) => {
      if (!isAirlockRequestsV3RoutesEnabled()) {
        return Promise.resolve()
      }

      return rejectDataAccessRequest(requestId, reason)
    },
    onSuccess: () => {
      toast({ message: 'Data access request rejected successfully', type: 'success' })
      queryClient.invalidateQueries({ queryKey: DataAccessRequestQueryKeys.Verification(requestId) })
      queryClient.invalidateQueries({ queryKey: DataAccessRequestQueryKeys.All() })
      invalidateRequestActivityQueries(queryClient)
      onSuccess?.()
    },
    onError: error => {
      toast({ message: extractErrorMessage(error, 'Failed to reject data access request'), type: 'error' })
    }
  })
}
